import { cn } from '../lib/utils';

export type TypewriterProps = {
  /** The whole sentence. */
  text: string;
  /**
   * How many of its characters are showing. The component keeps no clock of
   * its own: the caller drives it, usually with `typedLength` from
   * `lib/typing` against a `useStepTimer`, so the typing can stay in step with
   * whatever else that clock moves.
   */
  typed: number;
  /**
   * Draws the caret after the last typed character: solid while typing, then
   * blinking once the sentence is done.
   */
  caret?: boolean;
  className?: string;
};

/**
 * A sentence that types itself out.
 *
 * The untyped rest is drawn too, invisible, so the sentence takes its full
 * space from the first character and wraps where it finally will: nothing
 * below it moves while it types, and a word never starts on one line only to
 * jump to the next as it grows.
 *
 * A screen reader gets the whole sentence at once, from a hidden copy; the
 * typed half, the caret and the invisible rest are all `aria-hidden`, so a
 * partial sentence is never announced.
 */
export function Typewriter({ text, typed, caret = true, className }: TypewriterProps) {
  const shown = Math.min(Math.max(typed, 0), text.length);
  const done = shown === text.length;
  // The caret sits after the last typed letter, before any space typed after
  // it. A line may break after a space, and an anchor left there would go down
  // with the next word: the caret would start the next line, ahead of letters
  // still on the line above.
  const head = text.slice(0, shown);
  const letters = head.trimEnd();

  return (
    <span className={className}>
      <span className="sr-only">{text}</span>
      <span aria-hidden="true">
        {letters}
        {caret && (
          // An empty plain inline as the anchor, not an inline-block: an
          // atomic box between two letters is a place the line may break, and
          // the word being typed would split around the caret. This one has
          // no width and no break opportunity; the bar hangs off it.
          <span className="relative">
            <span
              className={cn(
                'absolute top-0 left-px h-[1.05em] w-0.5 bg-brand-text',
                done && 'animate-caret-blink motion-reduce:animate-none',
              )}
            />
          </span>
        )}
        {head.slice(letters.length)}
        <span className="opacity-0">{text.slice(shown)}</span>
      </span>
    </span>
  );
}
